import * as THREE from 'three';
import { AircraftView } from './Aircraft.js';
import { getAircraftType } from './AircraftCatalog.js';
import { FlightAudio } from './Audio.js';
import {
  createBattleState,
  firePlayerWeapon,
  getAgent,
  startBattle,
  syncPlayerAgent,
  updateBattle
} from './BattleModel.js';
import { CameraRig } from './CameraRig.js';
import { Effects } from './Effects.js';
import { createFlightState, updateFlight } from './FlightModel.js';
import { InputController } from './Input.js';
import { AdaptiveQualityController, getBrowserPerformanceProfile } from './Performance.js';
import { GameUI } from './UI.js';
import { terrainHeight, World } from './World.js';

export class Game {
  constructor({ renderer, canvas, root }) {
    this.renderer = renderer;
    this.canvas = canvas;
    this.root = root;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(58, window.innerWidth / window.innerHeight, 0.3, 22000);
    this.clock = new THREE.Clock();
    this.elapsed = 0;
    this.started = false;
    this.paused = false;
    this.finished = false;
    this.simulationAccumulator = 0;
    this.uiAccumulator = 0;
    this.aircraftViews = new Map();
    this.performanceProfile = renderer.userData.performanceProfile
      ?? getBrowserPerformanceProfile();
    this.lowPower = this.performanceProfile.lowPower;
    this.qualityController = new AdaptiveQualityController(
      this.performanceProfile,
      window.devicePixelRatio
    );
    this.frameMetrics = {
      frames: 0,
      maxFrameDtMs: 0,
      maxSimulationSteps: 0,
      droppedSimulationSeconds: 0
    };

    this.configureRenderer();
    this.world = new World(this.scene, { lowPower: this.lowPower });
    this.effects = new Effects(this.scene, { lowPower: this.lowPower });
    this.audio = new FlightAudio();
    this.cameraRig = new CameraRig(this.camera);
    this.battle = createBattleState();
    this.flight = createFlightState();

    const actions = {
      onStart: (config) => this.start(config),
      onRestart: () => this.restart(),
      onReturnToIntro: () => this.returnToIntro(),
      onPause: () => this.togglePause(),
      onCamera: () => this.cycleCamera()
    };
    this.ui = new GameUI(root, actions, this.performanceProfile);
    this.input = new InputController(root, actions);
    this.setupAircraftViews();
    this.bindWindow();
    this.ui.ready(renderer.userData.backend);
    this.renderer.setAnimationLoop(() => this.frame());
  }

  configureRenderer() {
    this.renderer.setPixelRatio(this.qualityController.currentRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = this.performanceProfile.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.04;
  }

  bindWindow() {
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setPixelRatio(
        this.qualityController.setDevicePixelRatio(window.devicePixelRatio)
      );
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.started && !this.finished) this.setPaused(true);
    });
  }

  setupAircraftViews() {
    this.aircraftViews.forEach((view) => view.dispose());
    this.aircraftViews.clear();
    for (const agent of this.battle.agents) {
      const view = new AircraftView(this.scene, {
        team: agent.team,
        typeId: agent.typeId,
        isPlayer: agent.isPlayer,
        lowPower: this.lowPower,
        castShadows: (
          this.performanceProfile.name !== 'mobile-premium' ||
          agent.isPlayer
        )
      });
      view.update(agent, 0);
      this.aircraftViews.set(agent.id, view);
    }
    this.aircraft = this.aircraftViews.get(this.battle.playerId);
  }

  async start(config = {}) {
    if (this.started && !this.finished) return;
    await this.audio.start();
    this.battle = startBattle(createBattleState(config));
    const player = getAgent(this.battle, this.battle.playerId);
    this.flight = createFlightState({
      position: { ...player.position },
      speed: player.speed,
      throttle: player.throttle,
      heading: player.heading,
      grounded: true,
      gearDown: true
    });
    this.setupAircraftViews();
    this.started = true;
    this.finished = false;
    this.paused = false;
    this.simulationAccumulator = 0;
    this.uiAccumulator = 0;
    this.cameraRig.modeIndex = 0;
    this.cameraRig.snap(this.aircraft.group);
    this.ui.start();
    this.ui.update(this.flight, this.battle, this.world.querySurface(0, 650), null);
    this.ui.toast('Both squadrons launching', '5 VS 5', 'blue');
  }

  restart() {
    const config = { blueType: this.battle.blueType, redType: this.battle.redType };
    this.started = false;
    this.start(config);
  }

  returnToIntro() {
    this.started = false;
    this.finished = false;
    this.paused = false;
    this.battle = createBattleState();
    this.flight = createFlightState();
    this.setupAircraftViews();
  }

  setPaused(value) {
    if (!this.started || this.finished) return;
    this.paused = value;
    this.ui.setPaused(value);
    if (!value) this.clock.getDelta();
  }

  togglePause() {
    this.setPaused(!this.paused);
  }

  cycleCamera() {
    if (!this.started) return;
    const mode = this.cameraRig.cycle();
    const labels = { chase: 'Chase view', close: 'Wing view', cockpit: 'Cockpit view' };
    this.ui.toast(labels[mode], '', 'blue');
  }

  processEvents(events) {
    for (const event of events) {
      const attacker = event.attackerId ? getAgent(this.battle, event.attackerId) : null;
      const target = event.targetId ? getAgent(this.battle, event.targetId) : null;
      if (event.type === 'shot') {
        const color = attacker?.team === 'red' ? '#ff4058' : '#4ca4ff';
        this.effects.tracer(event.from, event.to, color, event.weapon === 'missile');
        if (attacker?.isPlayer) this.audio.event(event.weapon === 'missile' ? 'missile' : 'gun');
      } else if (event.type === 'hit') {
        this.effects.burst(event.position, '#ffd081', 12, 9);
        if (target?.isPlayer) {
          this.ui.flashDamage();
          this.cameraRig.bump(1.4);
          this.audio.event('hit');
        }
      } else if (event.type === 'destroyed') {
        this.effects.burst(event.position, '#ff6c35', 72, 34);
        this.aircraftViews.get(event.targetId)?.setVisible(false);
        this.audio.event('destroyed');
        this.cameraRig.bump(target?.isPlayer ? 4.8 : 1.8);
        if (target?.isPlayer) {
          this.battle.phase = 'finished';
          this.battle.winner = 'red';
        } else if (attacker?.isPlayer) {
          this.ui.toast(`${target.callSign} destroyed`, '+1000', 'blue');
        }
      } else if (event.type === 'merge') {
        this.ui.toast('Merge', 'WEAPONS FREE', 'red');
        this.audio.event('merge');
      }
    }
  }

  finishBattle() {
    if (this.finished) return;
    this.finished = true;
    const success = this.battle.winner === 'blue';
    this.audio.event(success ? 'victory' : 'defeat');
    this.ui.showResult(this.battle, success);
  }

  updateSimulationStep(dt) {
    const surface = this.world.querySurface(this.flight.position.x, this.flight.position.z);
    const player = getAgent(this.battle, this.battle.playerId);
    const performance = getAircraftType(player.typeId);
    const controls = this.input.getControls();
    updateFlight(this.flight, controls, dt, { ...surface, performance });
    syncPlayerAgent(this.battle, this.flight);

    const events = updateBattle(this.battle, dt, { groundHeight: terrainHeight });
    if (controls.gun) firePlayerWeapon(this.battle, 'gun');
    if (controls.missile) firePlayerWeapon(this.battle, 'missile');
    const combinedEvents = [...events];
    for (const event of this.battle.events) {
      if (!combinedEvents.includes(event)) combinedEvents.push(event);
    }
    this.processEvents(combinedEvents);
    if (this.battle.phase === 'finished') this.finishBattle();
  }

  updatePresentation(dt) {
    for (const agent of this.battle.agents) {
      if (agent.isPlayer) continue;
      this.aircraftViews.get(agent.id)?.update(agent, dt);
    }
    this.aircraft.update(this.flight, dt);
    this.audio.update(this.flight);
    this.uiAccumulator += dt;
    const uiInterval = 1 / this.performanceProfile.radarHz;
    if (this.uiAccumulator < uiInterval) return;
    this.uiAccumulator %= uiInterval;
    const surface = this.world.querySurface(this.flight.position.x, this.flight.position.z);
    const target = getAgent(this.battle, this.battle.playerTargetId);
    this.ui.update(this.flight, this.battle, surface, target);
  }

  updateIntroCamera() {
    const drift = Math.sin(this.elapsed * 0.18);
    const compact = window.innerWidth < 700;
    this.camera.position.set(-34 + drift * 4, compact ? 16 : 13, compact ? 616 : 610);
    this.camera.lookAt(compact ? -8 : 2, 5, 675);
  }

  frame() {
    const dt = Math.min(this.clock.getDelta(), 0.5);
    const adaptiveRatio = this.qualityController.observeFrame(dt);
    if (adaptiveRatio !== null) {
      this.renderer.setPixelRatio(adaptiveRatio);
      this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    }
    this.frameMetrics.frames += 1;
    this.frameMetrics.maxFrameDtMs = Math.max(this.frameMetrics.maxFrameDtMs, dt * 1000);
    this.elapsed += dt;
    if (this.started && !this.paused && !this.finished) {
      const fixedStep = 1 / this.performanceProfile.simulationHz;
      const accumulatorLimit = fixedStep * this.performanceProfile.maxSimulationSteps;
      const pendingSimulation = this.simulationAccumulator + dt;
      if (pendingSimulation > accumulatorLimit) {
        this.frameMetrics.droppedSimulationSeconds += pendingSimulation - accumulatorLimit;
      }
      this.simulationAccumulator = Math.min(accumulatorLimit, pendingSimulation);
      let steps = 0;
      while (
        this.simulationAccumulator >= fixedStep &&
        steps < this.performanceProfile.maxSimulationSteps &&
        !this.finished
      ) {
        this.updateSimulationStep(fixedStep);
        this.simulationAccumulator -= fixedStep;
        steps += 1;
      }
      this.frameMetrics.maxSimulationSteps = Math.max(
        this.frameMetrics.maxSimulationSteps,
        steps
      );
      if (steps === this.performanceProfile.maxSimulationSteps && this.simulationAccumulator >= fixedStep) {
        this.frameMetrics.droppedSimulationSeconds += this.simulationAccumulator;
        this.simulationAccumulator %= fixedStep;
      }
      this.updatePresentation(dt);
    } else if (!this.started) {
      for (const agent of this.battle.agents) this.aircraftViews.get(agent.id)?.update(agent, dt);
    }

    const effectsStep = Math.min(dt, this.lowPower ? 1 / 30 : 1 / 45);
    this.effects.update(this.paused ? 0 : effectsStep);
    this.world.update(this.elapsed, this.paused ? 0 : dt);
    if (this.started && this.aircraft?.group.visible) this.cameraRig.update(this.aircraft.group, this.flight, dt);
    else if (!this.started) this.updateIntroCamera();
    this.renderer.render(this.scene, this.camera);
  }
}
